import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * React Query hooks for the country-overrides admin UI.
 *
 * Server contract: `apps/api/src/routes/admin/country-overrides.routes.ts`.
 * Mirrors the ransomware-groups + cpe-mappings hook shape.
 */

export interface CountryOverrideRow {
  id: string;
  entityType: "threat_actor";
  entityId: string;
  entityName: string | null;
  /** Current `threat_actors.origin_country` (uppercase α2) for visual diff. */
  currentOriginCountry: string | null;
  /** Lowercase α2 (matches storage convention). */
  overrideCountry: string;
  reason: string;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CountryOverridesPage {
  page: number;
  limit: number;
  total: number;
  rows: CountryOverrideRow[];
}

export interface CreateOverridePayload {
  entityType: "threat_actor";
  entityId: string;
  /** ISO α2 lowercase (e.g. "cn"). */
  overrideCountry: string;
  reason: string;
}

export interface UpdateOverridePayload {
  overrideCountry?: string;
  reason?: string;
}

const KEY = "admin-country-overrides";

export function useCountryOverrides(page: number, limit: number) {
  return useQuery({
    queryKey: [KEY, page, limit],
    queryFn: () =>
      api.get<CountryOverridesPage>(
        `/admin/country-overrides?page=${page}&limit=${limit}`,
      ),
  });
}

export function useCreateCountryOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOverridePayload) =>
      api.post<{ ok: true; id: string; createdAt: string }>(
        "/admin/country-overrides",
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useUpdateCountryOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateOverridePayload }) =>
      api.put<{ ok: true; id: string }>(
        `/admin/country-overrides/${id}`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useDeleteCountryOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: true; id: string }>(
        `/admin/country-overrides/${id}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
}
